import React, {
    ComponentType,
    CSSProperties,
    MutableRefObject,
    useMemo,
    useRef,
    useEffect,
    useCallback,
    useLayoutEffect,
    useState,
} from 'react';
import styles from './additional-info-renderer.module.css';
import { useVirtualizer } from '@tanstack/react-virtual';

/** Matches @tanstack/virtual-core's ScrollAlignment (not re-exported from react-virtual). */
type ScrollAlignment = 'start' | 'center' | 'end' | 'auto';
import { observer } from 'mobx-react-lite';
import { useAdditionalInfos } from '../hooks/use-additional-infos/additional-infos-provider';
import { SortDirection } from '../hooks/use-additional-infos/types';
import {
    getBottomAnchorSpacer,
    getEdgeDirections,
    getNewestInLoadedId,
    getOldestRowScrollTarget,
    getVirtualRowKey,
    hasVisibleChronologicalRow,
} from './scroll-geometry';
import { ReanchorResult } from '../hooks/use-additional-infos/use-additional-infos';
import { NEWER_LOADER_ROW_ID, OLDER_LOADER_ROW_ID, PINNED_DIVIDER_ROW_ID } from '../constants';
import { AdditionalInfoView } from '../types';
import { PinnedNarrativeRow } from '../event-panel-additional-infos/components/pinned-narrative-row';
import { PinnedDividerRow } from '../event-panel-additional-infos/components/pinned-divider-row';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { LoadingIndicator } from '../../common/components/loading-indicator';
import { VirtualizerContext } from './use-row-virtualizer';
import { useUpdateVisibleAsRead } from './use-update-visible-read';
import { useScrollAnchor } from './use-scroll-anchor';
import { useEstimateRowSize } from './use-estimate-row-size';
import { useScrollAndStabilize } from './use-scroll-and-stabilize';
import {
    useNarrativeKeyboardNav,
    NarrativeRowNavProps,
} from '../hooks/use-narrative-keyboard-nav/use-narrative-keyboard-nav';

const ANCHOR_THRESHOLD_PX = 4;

type PinnedDividerItem = { type: 'PINNED_DIVIDER'; id: typeof PINNED_DIVIDER_ROW_ID };
// In-list loader rows bracketing the chronological data. `newer` is
// adjacent to the newest entry (DESC above data, ASC below); `older` at the far
// end. Shown only when that direction has an unloaded page.
type EdgeLoaderItem = {
    type: 'EDGE_LOADER';
    edge: 'newer' | 'older';
    id: typeof NEWER_LOADER_ROW_ID | typeof OLDER_LOADER_ROW_ID;
};
type PinnedRowItem = AdditionalInfoView & { _rowType: 'pinned' };
type ChronologicalRowItem = AdditionalInfoView & { _rowType?: undefined };
type VirtualizerItem = PinnedRowItem | PinnedDividerItem | EdgeLoaderItem | ChronologicalRowItem;

// One construction for both loader memos so they can't drift.
const makeEdgeLoaderRow = (edge: 'newer' | 'older', show: boolean): EdgeLoaderItem[] =>
    show
        ? [
              {
                  type: 'EDGE_LOADER',
                  edge,
                  id: edge === 'newer' ? NEWER_LOADER_ROW_ID : OLDER_LOADER_ROW_ID,
              },
          ]
        : [];

// In-list loading row; hidden on page-fetch failure (retry UI owns that state).
function EdgeLoaderRow({
    edge,
    hidden,
    LoadingComponent,
}: {
    edge: 'newer' | 'older';
    hidden: boolean;
    LoadingComponent?: ComponentType;
}): React.JSX.Element | null {
    if (hidden) return null;
    return (
        <div data-testid={`NARRATIVE_LOADING_${edge.toUpperCase()}`}>
            {LoadingComponent ? <LoadingComponent /> : <LoadingIndicator />}
        </div>
    );
}

interface AdditionalInfoRowProps {
    currentUserId?: number;
    canDeleteInfos?: boolean;
    isPinned?: boolean;
    isScheduledEvent?: boolean;
    isBolo?: boolean;
    onOpenEvent?: (id: number) => void;
    focusNarrativeInput?: () => void;
}
export interface AdditionalInfoRendererProps extends AdditionalInfoRowProps {
    height?: CSSProperties['height'];
    width?: CSSProperties['width'];
    markVisibleAsRead?: boolean;
    rowComponent: ComponentType<
        {
            data: AdditionalInfoView;
            index: number;
            agencyEventNumberToIdMap?: Record<string, number>;
            isProQA?: boolean;
            rowNavProps?: NarrativeRowNavProps;
        } & AdditionalInfoRowProps
    >;
    loadingComponent?: ComponentType;
    addonComponent?: ComponentType<{ height?: number; width?: number }>;
    onAllRead?: (id: number) => void;
    overscan?: number;
    forceReload?: boolean;
    isReadOnlyMode?: boolean;
    isFirstResponder?: boolean;
    jumpHandlersRef?: MutableRefObject<{
        jumpToNewest: () => void;
        jumpToOldest: () => void;
    } | null>;
    onEdgeStateChange?: (state: {
        isAtTop: boolean;
        isAtBottom: boolean;
        isScrollable: boolean;
    }) => void;
    hasInlineJumpButtons?: boolean;
}

export const AdditionalInfoRenderer = observer(
    ({
        rowComponent: RowComponent,
        loadingComponent: LoadingComponent,
        addonComponent: AddonComponent,
        markVisibleAsRead = true,
        onAllRead,
        overscan = 0,
        forceReload,
        jumpHandlersRef,
        onEdgeStateChange,
        hasInlineJumpButtons = false,
        ...rest
    }: AdditionalInfoRendererProps) => {
        const scrollContainerRef = useRef<HTMLDivElement | null>(null);
        const suppressSnapBackRef = useRef(false);
        const pendingScrollCancelRef = useRef<(() => void) | undefined>(undefined);
        // Stick-to-newest mode. The ref is the synchronous truth (read by
        // the re-anchor useLayoutEffect and the edge-fetch effects); the
        // state exists so effects re-fire when the anchor engages/disengages.
        // Two sources because the virtualizer's native scroll listener can
        // trigger a sync re-render before React processes our state update.
        const isAnchorEngagedRef = useRef(true);
        const [isAnchoredToNewest, setIsAnchoredToNewest] = useState(true);
        const lastAnchorScrollTopRef = useRef(0);
        const [scrollContainerClientHeight, setScrollContainerClientHeight] = useState(0);
        // Tracks whether scrollTop is within ANCHOR_THRESHOLD_PX of each edge.
        // Updated by the native scroll listener (which fires on every scroll
        // event) and used as a dep on the edge-fetch effects. Necessary
        // because firstVirtualItemIndex / lastVirtualItemIndex don't change
        // between e.g. scrollTop=5 and scrollTop=0 (overscan=0, ~28px rows
        // → the same virtual items are visible) — so without this signal the
        // effect wouldn't re-fire when the user crosses the threshold.
        const [edgeState, setEdgeState] = useState<{
            atTop: boolean;
            atBottom: boolean;
        }>({ atTop: true, atBottom: true });

        // Re-baseline the drift reference to the current scrollTop so the
        // engagement isn't immediately interpreted as a user-initiated drift.
        const engageAnchorToNewest = useCallback(() => {
            const el = scrollContainerRef.current;
            if (el !== null) {
                lastAnchorScrollTopRef.current = el.scrollTop;
            }
            isAnchorEngagedRef.current = true;
            setIsAnchoredToNewest(true);
        }, []);
        const {
            list: data,
            agencyEventNumberToIdMap,
            fetchNextPage,
            hasNextPage,
            hasPrevPage,
            isFetchingNextPage,
            pageSize,
            pageFetchFailed,
            initialSocketConnectionMade,
            reloadPage,
            isPageReloading,
            visiblePinnedEntries,
            showPinnedDivider,
            hiddenPinnedCount,
            isPinnedSectionExpanded,
            togglePinnedSection,
            pendingScrollTargetId,
            clearPendingScrollTarget,
            flashMessage,
            getIndexById,
            reanchorAtMessage,
            jumpToNewest,
            jumpToOldest,
            sortDirection,
            latestKnownId,
            oldestEntryId,
        } = useAdditionalInfos();

        const jumpIntentRef = useRef<{ targetId: number; mode: 'newest' | 'oldest' } | null>(null);
        const latestKnownIdRef = useRef(latestKnownId);
        latestKnownIdRef.current = latestKnownId;
        const oldestEntryIdRef = useRef(oldestEntryId);
        oldestEntryIdRef.current = oldestEntryId;

        const { rollOutCadNarrativeFilteringAndFocusImprovementsEnabled } = useEnabledFeatures();

        const isAscending = sortDirection === SortDirection.ASC;

        // Whether the store still has an unloaded page on the *newer* side
        // (DESC: top edge; ASC: bottom edge). Only ever true after a jump
        // (aroundId) — normal scroll never populates the newer cursor. Read
        // through a ref so the native scroll listener (attached once) sees the
        // live value without re-subscribing on every page-flag change.
        const hasUnloadedNewerPage = isAscending ? hasNextPage : hasPrevPage;
        const hasUnloadedNewerPageRef = useRef(hasUnloadedNewerPage);
        hasUnloadedNewerPageRef.current = hasUnloadedNewerPage;

        const pinnedRows: VirtualizerItem[] = useMemo(
            () =>
                rollOutCadNarrativeFilteringAndFocusImprovementsEnabled
                    ? visiblePinnedEntries.map((entry) => ({
                          ...entry,
                          _rowType: 'pinned' as const,
                      }))
                    : [],
            [rollOutCadNarrativeFilteringAndFocusImprovementsEnabled, visiblePinnedEntries]
        );

        const divider: VirtualizerItem[] = useMemo(
            () =>
                rollOutCadNarrativeFilteringAndFocusImprovementsEnabled && showPinnedDivider
                    ? [{ type: 'PINNED_DIVIDER' as const, id: PINNED_DIVIDER_ROW_ID }]
                    : [],
            [rollOutCadNarrativeFilteringAndFocusImprovementsEnabled, showPinnedDivider]
        );

        // Edge loaders, shown when that direction has an unloaded page. Flag-gated:
        // flag-off keeps the legacy phantom bottom loader (shouldShowBottomLoader).
        const newerLoader: EdgeLoaderItem[] = useMemo(
            () =>
                makeEdgeLoaderRow(
                    'newer',
                    rollOutCadNarrativeFilteringAndFocusImprovementsEnabled &&
                        (isAscending ? hasNextPage : hasPrevPage)
                ),
            [
                rollOutCadNarrativeFilteringAndFocusImprovementsEnabled,
                isAscending,
                hasNextPage,
                hasPrevPage,
            ]
        );
        const olderLoader: EdgeLoaderItem[] = useMemo(
            () =>
                makeEdgeLoaderRow(
                    'older',
                    rollOutCadNarrativeFilteringAndFocusImprovementsEnabled &&
                        (isAscending ? hasPrevPage : hasNextPage)
                ),
            [
                rollOutCadNarrativeFilteringAndFocusImprovementsEnabled,
                isAscending,
                hasNextPage,
                hasPrevPage,
            ]
        );

        // DESC: pinned at top, newest just below; ASC: pinned at bottom, newest
        // just above. data.length is an explicit dep — `data` is a MobX array with
        // a stable ref that wouldn't otherwise re-trigger the memo.
        const combinedData: VirtualizerItem[] = useMemo(
            () =>
                isAscending
                    ? [...olderLoader, ...data, ...newerLoader, ...divider, ...pinnedRows]
                    : [...pinnedRows, ...divider, ...newerLoader, ...data, ...olderLoader],
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [isAscending, pinnedRows, divider, newerLoader, olderLoader, data, data.length]
        );

        const getItemKey = useCallback(
            (index: number) => {
                const isLoaderRow = index === combinedData.length;
                if (isLoaderRow) return index;
                return getVirtualRowKey(combinedData[index]);
            },
            [combinedData]
        );

        const { estimateRowSize } = useEstimateRowSize(data, scrollContainerRef);

        // Legacy phantom loader (flag-OFF only); flag-ON uses the in-list loaders.
        const shouldShowBottomLoader =
            !rollOutCadNarrativeFilteringAndFocusImprovementsEnabled && hasNextPage;

        const rowVirtualizer = useVirtualizer({
            count: shouldShowBottomLoader ? combinedData.length + 1 : combinedData.length,
            getItemKey,
            getScrollElement: () => scrollContainerRef.current,
            estimateSize: estimateRowSize,
            overscan,
        });

        // Rows before data (the header offset for every index→data mapping):
        // DESC's `newer` loader precedes data; ASC's `older` loader precedes data.
        const chronologicalStartIndex = isAscending
            ? olderLoader.length
            : pinnedRows.length + divider.length + newerLoader.length;

        // Combined-list index of the `newer` loader sentinel (-1 if no newer page).
        // The newer-edge fetch triggers when this row scrolls into view, so it
        // fires at the chronological edge even behind a tall pinned section.
        const newerLoaderCombinedIndex =
            newerLoader.length === 0
                ? -1
                : isAscending
                ? chronologicalStartIndex + data.length
                : chronologicalStartIndex - newerLoader.length;

        useScrollAnchor({
            data,
            rowVirtualizer,
            chronologicalStartIndex,
            // The ref (not the state) is the synchronous truth; the state can lag
            // a frame behind after a native-scroll-driven re-render.
            isStuckToNewest: isAnchorEngagedRef.current,
        });

        const virtualItems = rowVirtualizer.getVirtualItems();

        // Used by the legacy (flag-off) bottom-edge effect only.
        const lastVirtualItem = virtualItems[virtualItems.length - 1];

        // Edge-fetch effect deps: re-fire the sentinel trigger as the window
        // scrolls (edgeState only tracks the absolute edges, which a tall pinned
        // section never reaches).
        const firstVirtualItemIndex = virtualItems[0]?.index;
        const lastVirtualItemIndex = lastVirtualItem?.index;

        // ResizeObserver-driven measurement updates flow through totalSize →
        // it's a dep on the re-anchor useLayoutEffect, which is how that
        // effect converges on the actual bottom without a retry counter.
        const totalSize = rowVirtualizer.getTotalSize();

        // Iteratively scroll to (index, align) until layout converges. Extracted
        // verbatim into useScrollAndStabilize; keeps the same
        // [rowVirtualizer, combinedData.length] dep semantics. See that hook for
        // the DOM-max trick, double-rAF convergence, and wall-clock cap rationale.
        const scrollAndStabilize = useScrollAndStabilize({
            rowVirtualizer,
            combinedDataLength: combinedData.length,
            scrollContainerRef,
            suppressSnapBackRef,
        });

        const scrollToOldestRow = useCallback(
            (onSettle?: () => void): (() => void) | undefined => {
                const target = getOldestRowScrollTarget({
                    isAscending,
                    chronologicalStartIndex,
                    dataLength: data.length,
                });
                if (target === undefined) {
                    onSettle?.();
                    return undefined;
                }
                return scrollAndStabilize(target.index, target.align, onSettle);
            },
            [isAscending, chronologicalStartIndex, data, scrollAndStabilize]
        );

        const scrollToNewestRow = useCallback((): void => {
            if (data.length === 0) return;
            engageAnchorToNewest();
        }, [data, engageAnchorToNewest]);

        const scrollAfterReanchor = useCallback(
            (result: ReanchorResult, align: ScrollAlignment = 'start', onSettle?: () => void) => {
                if (!result.inStore) return;
                scrollAndStabilize(result.index + chronologicalStartIndex, align, onSettle);
            },
            [scrollAndStabilize, chronologicalStartIndex]
        );

        const flashIntentRef = useRef<number | undefined>(undefined);
        const flashIfIntended = useCallback(
            (id: number | undefined): void => {
                if (id !== undefined && flashIntentRef.current === id) {
                    flashIntentRef.current = undefined;
                    flashMessage(id);
                }
            },
            [flashMessage]
        );

        const handleJumpToNewest = useCallback((): void => {
            const targetId = latestKnownIdRef.current;
            if (targetId !== undefined) {
                jumpIntentRef.current = { targetId, mode: 'newest' };
            }
            jumpToNewest();
            scrollToNewestRow();
        }, [jumpToNewest, scrollToNewestRow]);

        const handleJumpToOldest = useCallback((): void => {
            const targetId = oldestEntryIdRef.current;
            if (targetId !== undefined) {
                jumpIntentRef.current = { targetId, mode: 'oldest' };
            }
            jumpToOldest();
            scrollToOldestRow();
        }, [jumpToOldest, scrollToOldestRow]);

        useLayoutEffect(
            function syncJumpHandlersRef() {
                if (jumpHandlersRef !== undefined) {
                    jumpHandlersRef.current = {
                        jumpToNewest: handleJumpToNewest,
                        jumpToOldest: handleJumpToOldest,
                    };
                }
            },
            [jumpHandlersRef, handleJumpToNewest, handleJumpToOldest]
        );

        // Clicking a pinned entry scrolls to its chronological position via the
        // shared reanchorAtMessage primitive. If the target is
        // outside the loaded window, reanchorAtMessage kicks an aroundId fetch and
        // scrollToPendingTarget refines the scroll once the page lands. Either way
        // the target flashes once the scroll settles (onSettle for the in-window
        // case; the pending-target effect for the aroundId case) so it fires only
        // when the row is actually on screen.
        const handleScrollToPinned = useCallback(
            (info: AdditionalInfoView) => {
                flashIntentRef.current = info.id;
                scrollAfterReanchor(reanchorAtMessage(info.id), 'start', () =>
                    flashIfIntended(info.id)
                );
            },
            [reanchorAtMessage, scrollAfterReanchor, flashIfIntended]
        );

        const keyboardNavEnabled = rollOutCadNarrativeFilteringAndFocusImprovementsEnabled;

        const { navKeys, navIndexByKey } = useMemo(() => {
            const keys: string[] = [];
            const indexByKey = new Map<string, number>();
            combinedData.forEach((item, index) => {
                if ('type' in item && item.type === 'EDGE_LOADER') return;
                const key = String(getVirtualRowKey(item));
                keys.push(key);
                indexByKey.set(key, index);
            });
            return { navKeys: keys, navIndexByKey: indexByKey };
        }, [combinedData]);

        const scrollNavRowIntoView = useCallback(
            (key: string) => {
                const index = navIndexByKey.get(key);
                if (index === undefined) return;
                scrollAndStabilize(index, 'center');
            },
            [navIndexByKey, scrollAndStabilize]
        );

        const preferredNavKey = useMemo(
            () => (pinnedRows.length > 0 ? String(getVirtualRowKey(pinnedRows[0])) : undefined),
            [pinnedRows]
        );

        const narrativeKeyboardNav = useNarrativeKeyboardNav({
            orderedKeys: navKeys,
            enabled: keyboardNavEnabled,
            scrollRowIntoView: scrollNavRowIntoView,
            preferredKey: preferredNavKey,
        });

        // useLayoutEffect (not useEffect) so the scroll correction lands
        // before paint — avoids a one-frame flicker at the top before
        // catching up. The browser handles measurement convergence
        // automatically: ResizeObserver updates → getTotalSize grows →
        // re-render → this effect re-fires with the new totalSize dep.
        useLayoutEffect(
            function reanchorToNewest() {
                if (!rollOutCadNarrativeFilteringAndFocusImprovementsEnabled) return;
                if (!isAnchorEngagedRef.current) return;
                if (pendingScrollTargetId !== undefined) return;
                const el = scrollContainerRef.current;
                if (el === null) return;

                const target = isAscending ? el.scrollHeight - el.clientHeight : 0;
                if (Math.abs(el.scrollTop - target) < 1) {
                    lastAnchorScrollTopRef.current = el.scrollTop;
                    return;
                }
                rowVirtualizer.scrollToOffset(target, { align: 'start' });
                lastAnchorScrollTopRef.current = target;
            },
            [
                rollOutCadNarrativeFilteringAndFocusImprovementsEnabled,
                isAnchoredToNewest,
                isAscending,
                pendingScrollTargetId,
                totalSize,
                combinedData.length,
                rowVirtualizer,
                scrollContainerClientHeight,
                hasInlineJumpButtons,
            ]
        );

        // Native listener (not React onScroll) because the virtualizer
        // attaches its own native listener that triggers sync re-renders;
        // the synthetic handler would lose the race in React 17 and leave
        // isAnchorEngagedRef stale during the re-anchor effect.
        useEffect(
            function attachNativeScrollListener() {
                if (!rollOutCadNarrativeFilteringAndFocusImprovementsEnabled) return;
                const el = scrollContainerRef.current;
                if (el === null) return;

                // Shared helper — reads current scroll geometry and updates edgeState.
                // Called by both the scroll event and the content ResizeObserver so
                // the two sources stay in sync without duplicating the computation.
                const updateEdgeState = (): void => {
                    const atTop = el.scrollTop < ANCHOR_THRESHOLD_PX;
                    const atBottom =
                        el.scrollHeight - el.scrollTop - el.clientHeight < ANCHOR_THRESHOLD_PX;
                    setEdgeState((prev) =>
                        prev.atTop === atTop && prev.atBottom === atBottom
                            ? prev
                            : { atTop, atBottom }
                    );
                };

                const scrollHandler = (): void => {
                    const drift = Math.abs(el.scrollTop - lastAnchorScrollTopRef.current);
                    const atTop = el.scrollTop < ANCHOR_THRESHOLD_PX;
                    const atBottom =
                        el.scrollHeight - el.scrollTop - el.clientHeight < ANCHOR_THRESHOLD_PX;
                    const atNewest = isAscending ? atBottom : atTop;
                    // Guard against stranding the user after a jump:
                    // while a newer page is still unloaded, neither edge-proximity
                    // (atNewest) nor drift returning to the last anchor position may
                    // re-engage stick-to-newest. Either one would block the
                    // newer-edge fetch, because the physical newer edge is not the
                    // newest content until every newer page has loaded.
                    const shouldEngage =
                        (drift <= ANCHOR_THRESHOLD_PX || atNewest) &&
                        !hasUnloadedNewerPageRef.current;
                    if (isAnchorEngagedRef.current !== shouldEngage) {
                        isAnchorEngagedRef.current = shouldEngage;
                        setIsAnchoredToNewest(shouldEngage);
                    }
                    updateEdgeState();
                };

                el.addEventListener('scroll', scrollHandler, { passive: true });

                // Two resize sources that change scroll geometry:
                //
                // 1. Scroll container (el) — clientHeight changes when external layout
                //    shifts (jump buttons appearing, panel resize). Tracked via
                //    setScrollContainerClientHeight so reanchorToNewest re-fires and
                //    recomputes the correct "at bottom" offset.
                //
                // 2. Virtualizer inner div (firstElementChild) — height === totalSize,
                //    changes when items are added or removed. The natural virtual-list
                //    signal; no separate useLayoutEffect or totalSize dep needed.
                const resizeObserver = new ResizeObserver(() => {
                    setScrollContainerClientHeight(el.clientHeight);
                    updateEdgeState();
                });
                resizeObserver.observe(el);
                const innerEl = el.firstElementChild;
                if (innerEl) resizeObserver.observe(innerEl);

                return () => {
                    el.removeEventListener('scroll', scrollHandler);
                    resizeObserver.disconnect();
                };
            },
            [rollOutCadNarrativeFilteringAndFocusImprovementsEnabled, isAscending]
        );

        useEffect(
            function propagateEdgeState() {
                if (!rollOutCadNarrativeFilteringAndFocusImprovementsEnabled) return;
                const atTop = edgeState.atTop && !hasPrevPage;
                const atBottom = edgeState.atBottom && !hasNextPage;
                // When both edges are simultaneously true (content fits in viewport),
                // show the button that matches the current anchor direction:
                // DESC (newest-at-top) → top is the anchor; ASC → bottom is the anchor.
                const atBothEdges = atTop && atBottom;
                onEdgeStateChange?.({
                    isAtTop: atTop && (!atBothEdges || !isAscending),
                    isAtBottom: atBottom && (!atBothEdges || isAscending),
                    isScrollable: !atBothEdges,
                });
            },
            [
                rollOutCadNarrativeFilteringAndFocusImprovementsEnabled,
                edgeState,
                hasPrevPage,
                hasNextPage,
                isAscending,
                onEdgeStateChange,
            ]
        );

        useEffect(
            function reEngageAnchorOnSortChange() {
                if (!rollOutCadNarrativeFilteringAndFocusImprovementsEnabled) return;
                engageAnchorToNewest();
            },
            [
                rollOutCadNarrativeFilteringAndFocusImprovementsEnabled,
                isAscending,
                engageAnchorToNewest,
            ]
        );

        const prevDataLengthForAnchorRef = useRef(data.length);
        useEffect(
            function reEngageAnchorOnEntityLoad() {
                if (!rollOutCadNarrativeFilteringAndFocusImprovementsEnabled) return;
                const prev = prevDataLengthForAnchorRef.current;
                prevDataLengthForAnchorRef.current = data.length;
                // A jump (go-to-oldest / Scroll-to-pinned) resets the store to 0
                // and reloads an aroundId window, so data also transitions 0→N —
                // but the user jumped AWAY from newest, so we must NOT stick back
                // to it. In ASC that re-engage would reanchor to the
                // bottom and fire a spurious newer-edge fetch. Only bootstrap
                // (no pending jump target) should engage on the first load.
                if (prev === 0 && data.length > 0 && pendingScrollTargetId === undefined) {
                    engageAnchorToNewest();
                }
            },
            [
                rollOutCadNarrativeFilteringAndFocusImprovementsEnabled,
                data.length,
                engageAnchorToNewest,
                pendingScrollTargetId,
            ]
        );

        // TODO: RND-61328 — remove once the narrative-filtering rollout is
        // global and the flag is retired. Until then, this is the bottom-edge
        // trigger for the flag-off path; triggerBottomEdgeFetch below owns
        // the flag-on path.
        useEffect(
            function triggerLegacyBottomEdgeFetch() {
                if (rollOutCadNarrativeFilteringAndFocusImprovementsEnabled) return;
                if (!lastVirtualItem) {
                    return;
                }

                if (
                    lastVirtualItem.index >= chronologicalStartIndex + data.length - 1 &&
                    hasNextPage &&
                    !isFetchingNextPage &&
                    !pageFetchFailed &&
                    initialSocketConnectionMade
                ) {
                    fetchNextPage();
                }
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [
                chronologicalStartIndex,
                data.length,
                fetchNextPage,
                hasNextPage,
                initialSocketConnectionMade,
                isFetchingNextPage,
                pageSize,
                lastVirtualItem,
            ]
        );

        // DESC: visual top = newer (CATCHUP), bottom = older (HISTORIC).
        // ASC:  visual top = older (HISTORIC), bottom = newer (CATCHUP).
        const { bottomEdgeDirection, topEdgeDirection } = getEdgeDirections(sortDirection);

        // edgeState (updated by the native scroll listener on every scroll
        // event) is the re-fire dep — firstVirtualItemIndex doesn't change
        // between e.g. scrollTop=5 and scrollTop=0 with overscan=0 + small
        // rows, so without this signal the effect would miss threshold
        // crossings. Body reads el.scrollTop directly for accuracy; edgeState
        // initial value can be stale before the first scroll event fires.
        //
        // Asymmetric anchor gate: triggerTopEdgeFetch is anchor-gated so the
        // ASC bootstrap doesn't loop-fetch older pages (firstVirtualItemIndex
        // reads stale-0 right after the re-anchor scroll). triggerBottomEdgeFetch
        // is NOT gated — content-fits-in-viewport in DESC needs the bootstrap
        // auto-fetch so the user can load older pages without explicit scroll
        // (the only way to disengage the anchor when there's no scrollable
        // area). The gates aren't symmetric because the failure modes aren't:
        // ASC's "newest at bottom" means the bootstrap re-anchor moves
        // scrollTop, racing the virtualizer's stale state; DESC's "newest at
        // top" doesn't scroll on bootstrap and has no race.
        useEffect(
            function triggerBottomEdgeFetch() {
                if (!rollOutCadNarrativeFilteringAndFocusImprovementsEnabled) return;
                if (pendingScrollTargetId !== undefined) return;
                const el = scrollContainerRef.current;
                if (el === null) return;
                const items = rowVirtualizer.getVirtualItems();
                // ASC bottom = newer: trigger on the loader sentinel so it fires at
                // the chronological edge behind a tall pinned section. DESC bottom =
                // older (no pinned below): keep the absolute edge (drives bootstrap).
                const isNewerEdge = isAscending;
                const reached = isNewerEdge
                    ? newerLoaderCombinedIndex >= 0 &&
                      items.some((it) => it.index === newerLoaderCombinedIndex)
                    : el.scrollHeight - el.scrollTop - el.clientHeight < ANCHOR_THRESHOLD_PX;
                if (
                    !reached ||
                    isFetchingNextPage ||
                    pageFetchFailed ||
                    !initialSocketConnectionMade ||
                    !hasNextPage
                ) {
                    return;
                }
                // Newer edge needs a visible data row to anchor compensation, else
                // the prepend re-fires and runs away.
                if (
                    isNewerEdge &&
                    data.length > 0 &&
                    !hasVisibleChronologicalRow(items, chronologicalStartIndex, data.length)
                ) {
                    return;
                }
                fetchNextPage({ direction: bottomEdgeDirection });
            },
            [
                rollOutCadNarrativeFilteringAndFocusImprovementsEnabled,
                edgeState.atTop,
                edgeState.atBottom,
                totalSize,
                fetchNextPage,
                hasNextPage,
                initialSocketConnectionMade,
                isFetchingNextPage,
                pageFetchFailed,
                bottomEdgeDirection,
                pendingScrollTargetId,
                rowVirtualizer,
                chronologicalStartIndex,
                data.length,
                isAscending,
                newerLoaderCombinedIndex,
                firstVirtualItemIndex,
                lastVirtualItemIndex,
            ]
        );

        useEffect(
            function triggerTopEdgeFetch() {
                if (!rollOutCadNarrativeFilteringAndFocusImprovementsEnabled) return;
                if (pendingScrollTargetId !== undefined) return;
                if (isAnchorEngagedRef.current) return;
                const el = scrollContainerRef.current;
                if (el === null) return;
                const items = rowVirtualizer.getVirtualItems();
                // DESC top = newer: trigger on the loader sentinel (see
                // triggerBottomEdgeFetch). ASC top = older (no pinned above): keep
                // the absolute edge.
                const isNewerEdge = !isAscending;
                const reached = isNewerEdge
                    ? newerLoaderCombinedIndex >= 0 &&
                      items.some((it) => it.index === newerLoaderCombinedIndex)
                    : el.scrollTop < ANCHOR_THRESHOLD_PX;
                if (
                    !reached ||
                    isFetchingNextPage ||
                    pageFetchFailed ||
                    !initialSocketConnectionMade ||
                    !hasPrevPage
                ) {
                    return;
                }
                // Newer edge needs a visible data row to anchor compensation (see
                // triggerBottomEdgeFetch).
                if (
                    isNewerEdge &&
                    data.length > 0 &&
                    !hasVisibleChronologicalRow(items, chronologicalStartIndex, data.length)
                ) {
                    return;
                }
                fetchNextPage({ direction: topEdgeDirection });
            },
            [
                rollOutCadNarrativeFilteringAndFocusImprovementsEnabled,
                edgeState.atTop,
                edgeState.atBottom,
                totalSize,
                fetchNextPage,
                initialSocketConnectionMade,
                isFetchingNextPage,
                pageFetchFailed,
                hasPrevPage,
                topEdgeDirection,
                pendingScrollTargetId,
                rowVirtualizer,
                chronologicalStartIndex,
                data.length,
                isAscending,
                newerLoaderCombinedIndex,
                firstVirtualItemIndex,
                lastVirtualItemIndex,
            ]
        );

        useEffect(
            function scrollToPendingTarget() {
                if (!rollOutCadNarrativeFilteringAndFocusImprovementsEnabled) return;
                if (pendingScrollTargetId === undefined) return;
                // Wait for the aroundId fetch to fully resolve — without
                // this, the effect runs at data.length=0 mid-reset and again
                // before isFetchingNextPage clears.
                if (isFetchingNextPage) return;

                if (data.length === 0) {
                    clearPendingScrollTarget();
                    return;
                }

                let cancelScroll: (() => void) | undefined;
                const localIndex = getIndexById(pendingScrollTargetId);
                if (localIndex !== undefined && localIndex >= 0) {
                    // Jump-to-newest / jump-to-oldest use edge-aware scroll so
                    // the item lands at the correct end of the viewport
                    // (top in DESC for newest, bottom in DESC for oldest, etc.).
                    // Arbitrary pinned entries use start-alignment (item visible
                    // at top of viewport — matches scrollAfterReanchor).
                    const intent = jumpIntentRef.current;
                    const jumpMode =
                        intent !== null && intent.targetId === pendingScrollTargetId
                            ? intent.mode
                            : null;
                    if (jumpMode !== null) {
                        jumpIntentRef.current = null;
                    }
                    if (jumpMode === 'newest') {
                        scrollToNewestRow();
                    } else if (jumpMode === 'oldest') {
                        cancelScroll = scrollToOldestRow(clearPendingScrollTarget);
                    } else {
                        const targetId = pendingScrollTargetId;
                        cancelScroll = scrollAndStabilize(
                            localIndex + chronologicalStartIndex,
                            'start',
                            () => {
                                clearPendingScrollTarget();
                                flashIfIntended(targetId);
                            }
                        );
                    }
                } else {
                    // Target not in loaded window (mock pin sentinels,
                    // deleted narratives). Pick the closest end by id.
                    const newestInLoaded = getNewestInLoadedId(data, isAscending)!;
                    if (pendingScrollTargetId > newestInLoaded) {
                        scrollToNewestRow();
                    } else {
                        cancelScroll = scrollToOldestRow(clearPendingScrollTarget);
                    }
                }
                // Store the cancel handle in a ref instead of returning it
                // from the effect — clearPendingScrollTarget triggers a re-render
                // whose cleanup would otherwise abort the rAF chain.
                pendingScrollCancelRef.current?.();
                if (cancelScroll !== undefined) {
                    pendingScrollCancelRef.current = cancelScroll;
                }
                // The stabilize paths pass clearPendingScrollTarget as onSettle, so
                // pending is held until the scroll lands — keeping the edge-fetch
                // gate engaged across the jump and closing the spurious-2nd-request
                // window after the aroundId reset. The newest path does
                // no stabilize scroll, so clear now.
                if (cancelScroll === undefined) {
                    clearPendingScrollTarget();
                }
            },
            [
                rollOutCadNarrativeFilteringAndFocusImprovementsEnabled,
                pendingScrollTargetId,
                data,
                isAscending,
                chronologicalStartIndex,
                getIndexById,
                scrollAndStabilize,
                scrollToOldestRow,
                scrollToNewestRow,
                clearPendingScrollTarget,
                flashIfIntended,
                isFetchingNextPage,
            ]
        );

        useEffect(
            () => () => {
                pendingScrollCancelRef.current?.();
            },
            []
        );

        useLayoutEffect(() => {
            // Window-focus quirk: when focus moves to a different event
            // window and back, DOM scrollTop resets to 0 but the virtualizer
            // doesn't know. Force them back in sync so we don't render blank
            // space. Gated on the anchor refs so we don't fight an active
            // scroll — see scrollAndStabilize and reanchorToNewest.
            if (suppressSnapBackRef.current) return;
            if (
                rollOutCadNarrativeFilteringAndFocusImprovementsEnabled &&
                isAnchorEngagedRef.current
            ) {
                return;
            }
            if (
                scrollContainerRef.current !== null &&
                rowVirtualizer &&
                scrollContainerRef.current.scrollTop !== rowVirtualizer.scrollOffset
            ) {
                rowVirtualizer.scrollToOffset(rowVirtualizer.scrollOffset, {
                    align: 'start',
                });
            }
        });

        // In ASC the input box is at the bottom and the list reads like a chat:
        // newest content sits just above the input. When the combined content
        // (narratives + divider + pinned) is shorter than the viewport — few or
        // zero narratives, or the list still loading while the pinned section is
        // already populated — the virtualizer would otherwise lay everything out
        // from the top, leaving a gap below. Anchor it to the bottom instead by
        // padding the top with the leftover space. DESC keeps top alignment
        // (newest at top, input at top), so no spacer there.
        // scrollContainerClientHeight is provided by the renderer (tracked via a
        // ResizeObserver on the scroll container) — reused here so the spacer
        // recomputes on resize without a second observer.
        const bottomAnchorSpacer = useMemo(
            () =>
                getBottomAnchorSpacer({
                    isAscending,
                    viewportHeight: scrollContainerClientHeight,
                    totalSize,
                }),
            [isAscending, scrollContainerClientHeight, totalSize]
        );

        const fullHeightScrollContentStyle = useMemo(
            (): CSSProperties => ({
                // Grow to the viewport when content is short so the spacer can
                // push the rows to the bottom; otherwise track the real content.
                height: totalSize + bottomAnchorSpacer,
                width: '100%',
                position: 'relative',
            }),
            [totalSize, bottomAnchorSpacer]
        );

        const virtualItemsStart = virtualItems[0]?.start;
        const translateVirtualItemsStyle = useMemo(
            (): CSSProperties =>
                virtualItemsStart === undefined
                    ? {}
                    : {
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          transform: `translateY(${virtualItemsStart + bottomAnchorSpacer}px)`,
                      },
            [virtualItemsStart, bottomAnchorSpacer]
        );

        useUpdateVisibleAsRead(markVisibleAsRead, rowVirtualizer, chronologicalStartIndex);

        useEffect(() => {
            if (forceReload) {
                reloadPage();
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [forceReload]);

        return (
            <VirtualizerContext.Provider value={rowVirtualizer}>
                <div className={styles['addon-component-wrapper']}>
                    <div
                        ref={scrollContainerRef}
                        className={styles['scroll-container']}
                        data-testid="ADDITIONAL_INFOS_SCROLL_CONTAINER"
                    >
                        <div style={fullHeightScrollContentStyle}>
                            <div style={translateVirtualItemsStyle}>
                                {/* Keyed on data.length (not combinedData) so a reset
                                    fetch shows the loader even when pinned rows keep
                                    combinedData non-empty. */}
                                {isPageReloading || (isFetchingNextPage && data.length === 0) ? (
                                    <LoadingIndicator />
                                ) : (
                                    virtualItems.map((virtualRow) => {
                                        const isLoaderRow =
                                            virtualRow.index > combinedData.length - 1;
                                        return (
                                            <div
                                                key={virtualRow.key}
                                                data-index={virtualRow.index}
                                                ref={rowVirtualizer.measureElement}
                                            >
                                                {!isLoaderRow
                                                    ? (() => {
                                                          const item = combinedData[
                                                              virtualRow.index
                                                          ] as VirtualizerItem;
                                                          if (
                                                              'type' in item &&
                                                              item.type === 'EDGE_LOADER'
                                                          ) {
                                                              return (
                                                                  <EdgeLoaderRow
                                                                      edge={item.edge}
                                                                      hidden={pageFetchFailed}
                                                                      LoadingComponent={
                                                                          LoadingComponent
                                                                      }
                                                                  />
                                                              );
                                                          }
                                                          if (
                                                              'type' in item &&
                                                              item.type === 'PINNED_DIVIDER'
                                                          ) {
                                                              return (
                                                                  <PinnedDividerRow
                                                                      hiddenCount={
                                                                          hiddenPinnedCount
                                                                      }
                                                                      isExpanded={
                                                                          isPinnedSectionExpanded
                                                                      }
                                                                      onToggle={() => {
                                                                          togglePinnedSection();
                                                                          engageAnchorToNewest();
                                                                      }}
                                                                      rowNavProps={
                                                                          keyboardNavEnabled
                                                                              ? narrativeKeyboardNav.getRowProps(
                                                                                    String(
                                                                                        getVirtualRowKey(
                                                                                            item
                                                                                        )
                                                                                    )
                                                                                )
                                                                              : undefined
                                                                      }
                                                                  />
                                                              );
                                                          }
                                                          if (
                                                              '_rowType' in item &&
                                                              item._rowType === 'pinned'
                                                          ) {
                                                              const pinnedIndex = pinnedRows.findIndex(
                                                                  (r) => r.id === item.id
                                                              );
                                                              const isLastPinned =
                                                                  pinnedIndex ===
                                                                  pinnedRows.length - 1;
                                                              return (
                                                                  <PinnedNarrativeRow
                                                                      data={
                                                                          item as AdditionalInfoView
                                                                      }
                                                                      isFirst={pinnedIndex === 0}
                                                                      isLast={
                                                                          isLastPinned &&
                                                                          !showPinnedDivider
                                                                      }
                                                                      isExpanded={
                                                                          isPinnedSectionExpanded
                                                                      }
                                                                      currentUserId={
                                                                          rest.currentUserId
                                                                      }
                                                                      isScheduledEvent={
                                                                          rest.isScheduledEvent
                                                                      }
                                                                      isBolo={rest.isBolo}
                                                                      isFirstResponder={
                                                                          rest.isFirstResponder
                                                                      }
                                                                      isReadOnlyMode={
                                                                          rest.isReadOnlyMode
                                                                      }
                                                                      agencyEventNumberToIdMap={
                                                                          agencyEventNumberToIdMap
                                                                      }
                                                                      onOpenEvent={rest.onOpenEvent}
                                                                      onScrollTo={
                                                                          handleScrollToPinned
                                                                      }
                                                                      focusNarrativeInput={
                                                                          rest.focusNarrativeInput
                                                                      }
                                                                      rowNavProps={
                                                                          keyboardNavEnabled
                                                                              ? narrativeKeyboardNav.getRowProps(
                                                                                    String(
                                                                                        getVirtualRowKey(
                                                                                            item
                                                                                        )
                                                                                    )
                                                                                )
                                                                              : undefined
                                                                      }
                                                                  />
                                                              );
                                                          }
                                                          return (
                                                              <RowComponent
                                                                  data={item as AdditionalInfoView}
                                                                  agencyEventNumberToIdMap={
                                                                      agencyEventNumberToIdMap
                                                                  }
                                                                  index={virtualRow.index}
                                                                  rowNavProps={
                                                                      keyboardNavEnabled
                                                                          ? narrativeKeyboardNav.getRowProps(
                                                                                String(
                                                                                    getVirtualRowKey(
                                                                                        item
                                                                                    )
                                                                                )
                                                                            )
                                                                          : undefined
                                                                  }
                                                                  {...rest}
                                                              />
                                                          );
                                                      })()
                                                    : !pageFetchFailed &&
                                                      (LoadingComponent ? (
                                                          <LoadingComponent />
                                                      ) : (
                                                          <LoadingIndicator />
                                                      ))}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </div>
                    {AddonComponent && <AddonComponent />}
                </div>
            </VirtualizerContext.Provider>
        );
    }
);

export default AdditionalInfoRenderer;
